import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Play, Trash2, Send } from 'lucide-react';

interface Task {
  id: string;
  description: string;
  priority: string;
  added: string;
  status: 'pending' | 'in_progress' | 'completed';
}

interface TaskQueueProps {
  serverUrl: string;
  onSendCommand: (cmd: string) => void;
  wsConnected: boolean;
}

export const TaskQueue: React.FC<TaskQueueProps> = ({ serverUrl, onSendCommand, wsConnected }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTask, setNewTask] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await fetch(`${serverUrl}/api/tasks`);
      const data = await res.json();
      setTasks(data.tasks || []);
    } catch (e) {
      console.error('Failed to fetch tasks:', e);
    }
  }, [serverUrl]);

  useEffect(() => {
    fetchTasks();
    const interval = setInterval(fetchTasks, 5000);
    return () => clearInterval(interval);
  }, [fetchTasks]);

  const addTask = async () => {
    if (!newTask.trim()) return;
    setLoading(true);
    try {
      await fetch(`${serverUrl}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: newTask.trim() })
      });
      setNewTask('');
      await fetchTasks();
    } catch (e) {
      console.error('Failed to add task:', e);
    }
    setLoading(false);
  };

  const deleteTask = async (id: string) => {
    try {
      await fetch(`${serverUrl}/api/tasks/${id}`, { method: 'DELETE' });
      await fetchTasks();
    } catch (e) {
      console.error('Failed to delete:', e);
    }
  };

  const clearAll = async () => {
    try {
      await fetch(`${serverUrl}/api/tasks`, { method: 'DELETE' });
      await fetchTasks();
    } catch (e) {
      console.error('Failed to clear:', e);
    }
  };

  // Send queue to Claude as a direct message
  const sendToClaude = async () => {
    if (tasks.length === 0) return;
    setLoading(true);

    try {
      // Format tasks as numbered list
      const taskList = tasks.map((t, i) => `${i + 1}. ${t.description}`).join('\n');
      const message = `Add these to your todo list and work through them:\n${taskList}`;

      // Send via callback
      if (wsConnected) {
        onSendCommand(message);
      }

      // Clear the queue
      await fetch(`${serverUrl}/api/tasks`, { method: 'DELETE' });
      await fetchTasks();
    } catch (e) {
      console.error('Failed to send:', e);
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col h-full bg-gray-900/50">
      {/* Header */}
      <div className="px-3 py-1.5 border-b border-gray-800/50 flex items-center justify-between">
        <span className="text-[11px] font-medium text-gray-400">Request Queue</span>
        <div className="flex items-center gap-1">
          <button
            onClick={sendToClaude}
            disabled={loading || tasks.length === 0}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-green-400 bg-green-500/10 hover:bg-green-500/20 transition-colors disabled:opacity-50"
            title="Send all to Claude"
          >
            <Send className="w-3 h-3" />
            <span>Start</span>
          </button>
          <span className="text-[10px] text-cyan-400 ml-1">{tasks.length}</span>
        </div>
      </div>

      {/* Add task input */}
      <div className="px-2 py-1.5 border-b border-gray-800/30">
        <div className="flex gap-1">
          <input
            type="text"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTask()}
            placeholder="Add request..."
            className="flex-1 bg-gray-800/50 border border-gray-700/50 rounded px-2 py-1 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
          />
          <button
            onClick={addTask}
            disabled={loading || !newTask.trim()}
            className="p-1 rounded bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Tasks list */}
      <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
        {tasks.map((task, idx) => (
          <div
            key={task.id}
            className="flex items-center justify-between px-2 py-1 rounded bg-gray-800/30 text-[11px] group"
          >
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <span className="text-gray-600">{idx + 1}.</span>
              <span className="text-gray-400 truncate">{task.description}</span>
            </div>
            <button
              onClick={() => deleteTask(task.id)}
              className="p-0.5 rounded text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {tasks.length === 0 && (
          <div className="text-center text-gray-600 text-[10px] py-2">Queue empty</div>
        )}
      </div>
    </div>
  );
};

export default TaskQueue;
